import { createFileRoute, notFound } from '@tanstack/react-router'
import BlogDetailPage from '#/pages/BlogDetailPage'
import NotFoundPage from '#/pages/NotFoundPage'
import PageLoader from '#/components/common/PageLoader'
import { blogDetailQueryOptions } from '#/features/blogs/hooks/useBlog'
import { blogsQueryOptions } from '#/features/blogs/hooks/useBlogs'
import { coursesQueryOptions } from '#/features/courses/hooks/useCourses'

export const Route = createFileRoute('/_home/blog/$blogId')({
  pendingMs: 0,
  pendingComponent: () => (
    <PageLoader
      title="Loading Article..."
      subtitle="Fetching article contents and scholar insights"
    />
  ),
  loader: async ({ context: { queryClient }, params }) => {
    if (!params.blogId) {
      throw notFound()
    }

    try {
      const blog = await queryClient.ensureQueryData(
        blogDetailQueryOptions(params.blogId),
      )
      queryClient.prefetchQuery(blogsQueryOptions({ limit: 4 }))
      queryClient.prefetchQuery(coursesQueryOptions({ limit: 5 }))
      return blog
    } catch (err: any) {
      // Only treat a genuine 404 response as "not found"
      // Network errors, timeouts (408), 500s → re-throw → hits errorComponent
      if (err?.status === 404) throw notFound()
      throw err
    }
  },
  notFoundComponent: () => <NotFoundPage type="blog" />,
  head: ({ loaderData, params }) => {
    const title =
      loaderData?.metaTitle ||
      loaderData?.title ||
      (params.blogId ? params.blogId.replace(/-/g, ' ') : 'Blog Post')
    const description =
      loaderData?.metaDescription ||
      loaderData?.excerpt ||
      "Islamic insights, Tajweed reflections, and Quran learning guidance from educators at Wa'allamah Academy."
    const image = loaderData?.coverImage

    return {
      meta: [
        {
          title: `${title} | Wa'allamah`,
        },
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:title',
          content: `${title} | Wa'allamah`,
        },
        {
          property: 'og:description',
          content: description,
        },
        ...(image ? [{ property: 'og:image', content: image }] : []),
        {
          name: 'twitter:title',
          content: `${title} | Wa'allamah`,
        },
        {
          name: 'twitter:description',
          content: description,
        },
      ],
    }
  },
  component: BlogDetailPage,
})
